import { isErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { SuccessAlert } from '@components/Alerts/SuccessAlert';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Button, ButtonType } from '@components/Button';
import { Container } from '@components/Container';
import { Input, Textarea } from '@components/FormComponents';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useCsrfCookie } from '@hooks/useCsrfCookie';
import Head from 'next/head';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

export default function RequestAccountDeletionPage(): ReactElement {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState<string>();
  const [verify, setVerify] = useState(false);
  const [success, setSuccess] = useState(false);

  const csrfToken = useCsrfCookie();

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      email: '',
      reason: '',
      verificationCode: '',
      confirm: false,
      csrfToken,
    },
  });

  const handleSubmit = async (e: {
    name: string;
    email: string;
    reason: string;
    verificationCode: string;
  }) => {
    setSubmitting(true);

    if (!verify && !e.verificationCode) {
      const sentEmailResponse = await fetch('/api/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: e.email }),
      });

      if (!sentEmailResponse.ok) {
        rhfMethods.reset();
        setError(true);
        setErrorText(
          'There was an issue sending an email to confirm your email address.'
        );
      } else {
        setVerify(true);
      }
      setSubmitting(false);
      return;
    }

    const requestResponse = await fetch('/api/customer/delete-account', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(e),
    }).then((res) => res.json());

    if (!isErrorResponse(requestResponse)) {
      setError(false);
      setSuccess(true);
    } else {
      setError(true);
      setSuccess(false);
      setErrorText(requestResponse.error.message.join('. '));
    }

    // on success/error, reset form and reset verify state to go to initial form phase
    rhfMethods.reset();
    setVerify(false);
    setSubmitting(false);
  };

  return (
    <>
      <Head>
        <title>Bulk Nutrients - Our Form to Request Account Deletion</title>
        <meta
          name="title"
          content="Bulk Nutrients - Our Form to Request Account Deletion"
        />
        <meta
          name="description"
          content="Want to delete your account with us? No worries! Fill out the form and our customer service will be in touch when it's complete."
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/request-account-deletion`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            {
              title: 'Request Account Deletion',
              path: '/request-account-deletion',
            },
          ]}
        />
      </Container>
      <Container as="main" id="main-content">
        <article className="my-0 max-w-[600px] justify-self-center">
          <h1>Request Account Deletion</h1>
          <Paragraph>
            If you’d like to request the deletion of your Bulk Nutrients
            account, please complete the form below.
          </Paragraph>
          <Paragraph>
            Our team will review your request according to the terms in our{' '}
            <TextLink href="/bulk-nutrients-privacy-policy">
              Privacy Policy
            </TextLink>{' '}
            and confirm via email once the action has been taken.
          </Paragraph>
          <Paragraph>This is not an immediate or automatic process.</Paragraph>
          <FormProvider {...rhfMethods}>
            <form onSubmit={rhfMethods.handleSubmit(handleSubmit)}>
              {!verify ? (
                <>
                  <Input label="Full Name" name="name" required type="text" />
                  <Input
                    label="Email Address"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                  />
                  <Textarea
                    label="Reason for Request (optional)"
                    name="reason"
                    rows={5}
                  />
                </>
              ) : (
                <>
                  <h2 className="mt-4">Verification Required</h2>
                  <div className="my-4 flex items-center justify-evenly gap-4">
                    <div className="flex flex-col gap-3">
                      <Paragraph>
                        We've sent you an email to confirm you are the owner of
                        this email address. Please enter the verification code
                        in the field below to update your password
                      </Paragraph>

                      <Input
                        label="Verification Code"
                        name="verificationCode"
                        type="number"
                        required
                      />
                      <Input
                        type="checkbox"
                        label="Acknowledgement"
                        name="confirm"
                        value="confirm"
                        required
                        description={
                          <>
                            I understand this is a request for account deletion,
                            and that Bulk Nutrients will confirm once the
                            process is complete.
                          </>
                        }
                      />
                    </div>
                  </div>
                </>
              )}

              <Button
                type={ButtonType.submit}
                buttonStyle="primary"
                disabled={submitting || !rhfMethods.formState.isValid}
                className="h-10"
              >
                {!verify
                  ? 'Submit Validation Request'
                  : 'Request Account Deletion'}
              </Button>
            </form>
          </FormProvider>
        </article>
        <ErrorAlert show={error} setShow={setError}>
          {errorText}
        </ErrorAlert>
        <SuccessAlert
          title="Received"
          message="Our customer service team has received your request! They'll be in touch as soon as it's been processed."
          show={success}
          setShow={setSuccess}
        />
      </Container>
    </>
  );
}

RequestAccountDeletionPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
